// Infrastructure profile dimensions. Edit options here to add or rename. New
// dimensions also need (a) ENV_MODIFIERS coverage in ./weights.ts, (b) an
// explanation file under content/explanations/environment/. Internal
// type/const names (EnvDimension, ENV_DIMENSIONS) are kept from the upstream
// engine even though this catalog is no longer about AI deployment context —
// it's the same per-selection modifier mechanism repurposed for
// infrastructure/organizational context.

import type { EnvAnswers, EnvDimensionId, UseCaseId } from "../shared/types";

export type EnvOption = {
  value: string;
  label: string;
  hint?: string;
};

export type EnvDimension = {
  id: EnvDimensionId;
  question: string;
  options: EnvOption[];
  multiSelect?: boolean;
  // A predicate that decides whether to show this dimension based on current
  // answers. Returning false hides the question entirely. Unused for v1 —
  // every infrastructure-profile question applies regardless of which tech
  // footprint tab it's answered under.
  visibleWhen?: (env: EnvAnswers, useCaseId: UseCaseId) => boolean;
};

export const ENV_DIMENSIONS: EnvDimension[] = [
  {
    id: "network_complexity",
    question: "How complex is your network topology?",
    options: [
      { value: "simple", label: "Simple",
        hint: "Single site or a small number of similar sites, flat or lightly segmented." },
      { value: "moderate", label: "Moderate",
        hint: "Multiple sites, some segmentation, a defined but not extensive WAN." },
      { value: "complex", label: "Complex",
        hint: "Many sites or regions, layered segmentation, multiple network domains to coordinate." },
      { value: "highly_complex", label: "Highly Complex",
        hint: "Global, multi-region, multi-business-unit infrastructure with significant M&A or legacy sprawl." },
    ],
  },
  {
    id: "cloud_onprem_mix",
    question: "What's your cloud / on-prem mix?",
    options: [
      { value: "all_on_prem", label: "All On-Prem",
        hint: "No public cloud in the infrastructure path." },
      { value: "hybrid", label: "Hybrid",
        hint: "A meaningful mix of on-prem and cloud-hosted infrastructure." },
      { value: "mostly_cloud", label: "Mostly Cloud",
        hint: "Cloud-first, with on-prem limited to a few remaining sites or systems." },
      { value: "multi_cloud", label: "Multi-Cloud",
        hint: "Infrastructure and workloads spread across more than one cloud provider." },
    ],
  },
  {
    id: "ai_agents_deployed",
    question: "Are AI agents deployed inside your environment today?",
    options: [
      { value: "none", label: "None",
        hint: "No AI agents with tool access or operational authority in production or pilot." },
      { value: "pilot", label: "Pilot",
        hint: "One or more agents running in limited pilot or proof-of-concept scope." },
      { value: "production", label: "Production",
        hint: "AI agents operating in production with real tool access or operational authority." },
    ],
  },
];

export function getEnvDimension(id: EnvDimensionId): EnvDimension | undefined {
  return ENV_DIMENSIONS.find((d) => d.id === id);
}

// A dimension the user was never shown must not score. `visibleWhen` governed
// rendering only — the store could otherwise keep a stale answer when a prior
// answer hid the question, and the multiplier loops iterate the raw answer
// map, so an invisible answer would silently move the score. Filtering here,
// on the way into scoring, makes that structurally impossible. Fixed-point
// loop because visibility can in principle chain across dimensions.
export function visibleEnvOnly(env: EnvAnswers, useCaseId: UseCaseId): EnvAnswers {
  let current = env;
  for (let i = 0; i < ENV_DIMENSIONS.length; i++) {
    const allowed = new Set(visibleEnvDimensions(current, useCaseId).map((d) => d.id));
    const next: EnvAnswers = {};
    for (const key of Object.keys(current) as EnvDimensionId[]) {
      if (allowed.has(key)) next[key] = current[key];
    }
    if (Object.keys(next).length === Object.keys(current).length) return next;
    current = next;
  }
  return current;
}

export function visibleEnvDimensions(
  env: EnvAnswers,
  useCaseId: UseCaseId,
): EnvDimension[] {
  return ENV_DIMENSIONS.filter(
    (d) => !d.visibleWhen || d.visibleWhen(env, useCaseId),
  );
}
